import React from 'react';
import styled from 'styled-components';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const PageWrapper = styled.div`
  min-height: calc(100vh - 300px);
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const HeroSection = styled.section`
  background: #232B3B;
  padding: 100px 20px 80px;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/650f1c8c6cf62f0b696c3d20_Lake2.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.15;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  letter-spacing: -1px;
`;

const PageSubtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 24px);
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;

const ContentSection = styled.section`
  padding: 80px 20px;
  background: white;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: clamp(28px, 3.5vw, 36px);
  font-weight: 700;
  color: #232B3B;
  margin-bottom: 30px;
  text-align: center;
`;

const ContentBlock = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 25px;
  }
`;

const Paragraph = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 25px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-left: 4px solid #ffd700;
  padding: 25px;
  border-radius: 10px;
  margin: 30px 0;
`;

const HighlightText = styled.p`
  font-size: 18px;
  color: #333;
  font-weight: 600;
  line-height: 1.6;
  margin: 0;
`;

const TransparencyNote = styled.div`
  background: #f8f9fa;
  border-radius: 15px;
  padding: 30px;
  margin-top: 40px;
  text-align: center;
`;

const NoteTitle = styled.h3`
  font-size: 24px;
  color: #232B3B;
  margin-bottom: 15px;
  font-weight: 700;
`;

const NoteText = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 80px 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: clamp(32px, 4vw, 42px);
  color: white;
  margin-bottom: 20px;
`;

const CTAText = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  padding: 20px 40px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  }
`;

export const HowWeGetPaid: React.FC = () => {
  return (
    <SharedLayout>
      <PageWrapper>
        <HeroSection>
          <HeroContent>
            <PageTitle>How We Get Paid</PageTitle>
            <PageSubtitle>
              Complete transparency about our compensation model
            </PageSubtitle>
          </HeroContent>
        </HeroSection>

        <ContentSection>
          <Container>
            <SectionTitle>We don't charge you anything, but we do get paid. How does that work?</SectionTitle>
            
            <ContentBlock>
              <Paragraph>
                Annuities are issued by insurance companies, and per industry custom, the insurance company pays a "street" commission to the agent. Therefore, although we do not charge clients directly for the sale of annuities or the development of the client's annuity plans, please know that when a person decides to become one of our "happy clients" we receive compensation directly from the insurance carrier, which covers our product selection and underwriting costs related to your annuity.
              </Paragraph>

              <HighlightBox>
                <HighlightText>
                  At no point during this process will we charge or attempt to collect from you any fees other than what we receive directly from the insurance company.
                </HighlightText>
              </HighlightBox>

              <Paragraph>
                Rest assured that at no point during this process will we charge or attempt to collect from you any fees other than what we receive directly from the insurance company. The backtesting and other information being provided to you is for your education and your analysis process and is absolutely free. We will not surprise you with any monthly fees or other costs for using the MyStatement or the MyAnnuityApp.com service, but in order to be completely transparent we want to reiterate that we do get paid commissions directly by the insurance companies if you end up purchasing a product.
              </Paragraph>
            </ContentBlock>

            <TransparencyNote>
              <NoteTitle>Our Commitment to Transparency</NoteTitle>
              <NoteText>
                We believe in complete transparency about how we're compensated. This allows you to make informed decisions with confidence, knowing exactly how our business model works.
              </NoteText>
            </TransparencyNote>
          </Container>
        </ContentSection>

        <CTASection>
          <Container>
            <CTATitle>Ready to Get Started?</CTATitle>
            <CTAText>
              Experience our free, no-obligation service and see why over 10,000 clients trust us with their retirement planning.
            </CTAText>
            <CTAButton onClick={() => window.location.href = '/contact'}>
              SCHEDULE YOUR FREE CONSULTATION
            </CTAButton>
          </Container>
        </CTASection>
      </PageWrapper>
    </SharedLayout>
  );
};